import { Component, OnInit, ViewChild } from '@angular/core';
import { UserManagementService } from '../../user-management/user-management.service';
import { Router, NavigationEnd } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';

/**
 * Shown when the account has no uploaded picture. C-E-O.png was used here, but
 * it is a stock photograph of a person, so an account with no picture appeared
 * to have someone else's face.
 */
const DEFAULT_AVATAR = 'assets/images/user.png';

@Component({
    selector: 'user-management-nav',
    templateUrl: './user-management-nav.component.html',
    styleUrls: ['./user-management-nav.component.css'],
    standalone: false
})
export class UserManagementNavComponent implements OnInit {
  @ViewChild('content') content;
  public user: any = { name: 'John Doe' }; // Example user object
  public siteinfo: any;
  sitename: any;
  avatarUrl: string = './assets/images/avathar.jpg'; // Default avatar image
  selectedFile: File | null = null;
  public imageSrc: SafeUrl | null = null; // Property to hold the sanitized image URL
  /** Object URL currently behind imageSrc, revoked whenever it is replaced. */
  private objectUrl: string | null = null;


  constructor(private router: Router, private userService: UserManagementService, private sanitizer: DomSanitizer, private http: HttpClient) {}

  ngOnInit(): void {
    this.refreshUserAndSite();
    const allSites = this.userService.getSiteDetail();
    this.user = this.userService.getUserDetail();
    this.siteinfo = this.userService.getSiteDetail();
    if (allSites?.length) {
      this.sitename = this.siteinfo[0].site_name;
    }
    const storedAvatar = localStorage.getItem('avatarUrl');
    if (storedAvatar) {
      this.avatarUrl = storedAvatar;
    }
    const user = this.userService.getUserDetail();
    if (user && user.id) {
      this.loadUserImage(user.id);
    } else {
      this.imageSrc = DEFAULT_AVATAR;
    }
    this.router.events
    .pipe(filter(event => event instanceof NavigationEnd))
    .subscribe(() => {
      this.refreshUserAndSite();
    });
     window.addEventListener('popstate', this.forceImageReloadOnPopState);
  }
ngOnDestroy(): void {
  window.removeEventListener('popstate', this.forceImageReloadOnPopState);
  if (this.objectUrl) {
    URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
  }
}

forceImageReloadOnPopState = () => {
  this.refreshUserAndSite();
}
  refreshUserAndSite() {
    const allSites = this.userService.getSiteDetail();
    this.user = this.userService.getUserDetail();
    this.siteinfo = this.userService.getSiteDetail();
    if (allSites?.length) {
      this.sitename = this.siteinfo[0].site_name;
    }
    if (this.user && this.user.id) {
      // Use the new dynamic endpoint with user_id
      this.loadUserImage(this.user.id);
    } else {
      this.imageSrc = DEFAULT_AVATAR;
    }
  }

  // Handle file selection
  onImageUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.uploadImage();
      // Clearing the value lets the same file be picked again: without it the
      // input holds that selection and no change event fires next time.
      input.value = '';
    }
  }

  /**
   * Point the avatar at a blob, releasing whatever it pointed at before.
   * Without the revoke every upload and every navigation leaked one.
   */
  private setImageFromBlob(blob: Blob): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
    this.objectUrl = URL.createObjectURL(blob);
    this.imageSrc = this.sanitizer.bypassSecurityTrustUrl(this.objectUrl);
  }

 // Upload the selected image
  loadUserImage(userId: string | number): void {
    // Use the direct user-image endpoint that works without CORS issues
    const imageUrl = `${environment.nodeUrl1}user-image/${userId}?t=${Date.now()}`;
    console.log('Loading user image from URL:', imageUrl);
    console.log('User ID:', userId);
    
    this.http.get(imageUrl, { responseType: 'blob' }).subscribe(
      (blob) => {
        console.log('✅ User image loaded successfully from:', imageUrl);
        console.log('Blob size:', blob.size);
        this.setImageFromBlob(blob);
      },
      (error) => {
        console.error('Error loading user image:', error);
        console.error('Image URL that failed:', imageUrl);
        // Fallback to default avatar if image not found
        this.imageSrc = DEFAULT_AVATAR;
      }
    );
  }

  uploadImage(): void {
  if (!this.selectedFile) {
    alert('Please select an image to upload.');
    return;
  }

  // Read the id fresh rather than trusting the cached `user`: the picture is
  // stored per user_id, so a stale one would overwrite another account's image
  // or be rejected outright. A CS expert switching into a plant changes who the
  // session is for, and this component outlives that change.
  const userId = this.userService.getUserDetail()?.id;
  if (!userId) {
    alert('Unable to identify the signed-in user. Please sign in again.');
    return;
  }

  const formData = new FormData();
  formData.append('image', this.selectedFile);
  formData.append('user_id', String(userId));

  const uploadUrl = `${environment.nodeUrl1}upload-image`;

  this.http.post(uploadUrl, formData).subscribe(
    (response: any) => {
      if (response && response.imageUrl) {
        // Extract the filename from the imageUrl
        const urlParts = response.imageUrl.split('/');
        const filename = urlParts[urlParts.length - 1];
        const user = this.userService.getUserDetail() || this.user || {};
        user.image = filename;
        this.userService.addUserDetail(user);
        this.user = user;

        // Show it straight away from the file that was just uploaded. The nav
        // already holds those bytes, so waiting on a second round trip only
        // left the old picture on screen until the page was reloaded.
        if (this.selectedFile) {
          this.setImageFromBlob(this.selectedFile);
        }

        // Then reconcile with what the server actually stored.
        this.loadUserImage(userId);

        alert('Image uploaded successfully!');
        this.refreshUserAndSite();
      } else {
        alert('Image uploaded, but no imageUrl returned.');
      }
    },
    (error) => {
      // The server explains refusals (file too large, unsupported type); a flat
      // "Failed to upload" left no way to tell those apart from an outage.
      alert(this.describeUploadFailure(error));
    }
  );
}

  /**
   * The server checks an image's actual bytes against its extension, so a file
   * saved in one format but named as another is refused. That reads as a raw
   * internal message, and it is a common case: screenshot tools often write PNG
   * data under a .jpg name.
   */
  private describeUploadFailure(error: any): string {
    const reason = String(error?.error?.message || error?.message || '');

    if (reason.toLowerCase().includes('does not match file content')) {
      return 'That file\'s contents do not match its file extension, so it was rejected. '
        + 'Re-save it in its real format (screenshot tools often write PNG data under a .jpg name) '
        + 'and try again.';
    }

    return reason ? `Failed to upload image: ${reason}` : 'Failed to upload image.';
  }

  logout() {
    this.userService.logout();
    sessionStorage.removeItem('visitedUserManagementSites');
    sessionStorage.removeItem('admin_id');
    this.router.navigate(['/login']);
  }
}
